import { useEffect } from 'react'
import { usePortal } from '@/context/PortalContext'

/* ==========================================================================
   usePortalTheme Hook
   Task F30: White-Label Theme System

   Injects pharmacy-specific CSS variables at runtime based on portal config.
   ========================================================================== */

/**
 * Default TJM brand colors (fallback when pharmacy hasn't configured custom colors)
 */
const DEFAULT_PRIMARY = '#5c288f' // deep-purple
const DEFAULT_SECONDARY = '#bba2fe' // lavender

/**
 * Calculates a darker shade of a hex color for hover states.
 * Darkens by approximately 10%.
 */
function darkenColor(hex: string, percent: number = 10): string {
  // Remove # if present
  const color = hex.replace('#', '')

  // Parse RGB values
  const r = parseInt(color.substring(0, 2), 16)
  const g = parseInt(color.substring(2, 4), 16)
  const b = parseInt(color.substring(4, 6), 16)

  // Darken each channel
  const factor = 1 - percent / 100
  const newR = Math.max(0, Math.floor(r * factor))
  const newG = Math.max(0, Math.floor(g * factor))
  const newB = Math.max(0, Math.floor(b * factor))

  // Convert back to hex
  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`
}

/**
 * Converts a hex color to an RGBA string with specified opacity.
 */
function hexToRgba(hex: string, alpha: number): string {
  const color = hex.replace('#', '')
  const r = parseInt(color.substring(0, 2), 16)
  const g = parseInt(color.substring(2, 4), 16)
  const b = parseInt(color.substring(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/**
 * Hook that applies pharmacy-specific theme colors to CSS custom properties.
 *
 * The hook reads the portal config and injects the pharmacy's primary and
 * secondary colors as CSS variables on the document root. This enables
 * white-labeling of:
 * - Primary buttons
 * - Links
 * - Focus rings
 * - Header/logo area
 *
 * @example
 * ```tsx
 * function App() {
 *   // Apply theme colors from portal config
 *   usePortalTheme()
 *
 *   return <div>...</div>
 * }
 * ```
 */
export function usePortalTheme(): void {
  const { config, loading } = usePortal()

  useEffect(() => {
    // Don't apply theme while loading (prevents flicker)
    if (loading) {
      return
    }

    // Get colors from config or use defaults
    const primaryColor = config?.primary_color || DEFAULT_PRIMARY
    const secondaryColor = config?.secondary_color || DEFAULT_SECONDARY

    // Calculate derived colors
    const primaryHover = darkenColor(primaryColor, 10)
    const secondaryFocusRing = hexToRgba(secondaryColor, 0.3)
    const primaryFocusRing = hexToRgba(primaryColor, 0.3)

    // Inject CSS custom properties
    const root = document.documentElement
    root.style.setProperty('--pharmacy-primary', primaryColor)
    root.style.setProperty('--pharmacy-primary-hover', primaryHover)
    root.style.setProperty('--pharmacy-secondary', secondaryColor)
    root.style.setProperty('--pharmacy-focus-ring', secondaryFocusRing)
    root.style.setProperty('--pharmacy-primary-focus-ring', primaryFocusRing)

    // Cleanup on unmount or config change
    return () => {
      root.style.removeProperty('--pharmacy-primary')
      root.style.removeProperty('--pharmacy-primary-hover')
      root.style.removeProperty('--pharmacy-secondary')
      root.style.removeProperty('--pharmacy-focus-ring')
      root.style.removeProperty('--pharmacy-primary-focus-ring')
    }
  }, [config, loading])
}

/**
 * Returns the current pharmacy theme values from the portal config.
 * Useful for components that need to access theme values programmatically.
 */
export function usePortalThemeValues() {
  const { config, loading } = usePortal()

  return {
    primaryColor: config?.primary_color || DEFAULT_PRIMARY,
    secondaryColor: config?.secondary_color || DEFAULT_SECONDARY,
    logoUrl: config?.logo_url || null,
    pharmacyName: config?.pharmacy_name || 'TJM Labs',
    loading,
  }
}

export default usePortalTheme
